import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Badge, Button } from '@/components/ui'
import { useAuthStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import {
  listPartnerCoverage,
  listTerritorialManagers,
  assignTerritorialManager,
  transferTerritorialManager,
  updateTerritorialManagerAssignmentStart,
} from '@/services/territorialManagerService'
import type { PartnerCoverageRow, TerritorialManagerListItem } from '@/types'
import { formatPartnerDisplay } from '@/lib/utils'
import { ArrowLeft, AlertTriangle, Search, Filter } from 'lucide-react'
import {
  listPartnerServiceCategories,
  PARTNER_SERVICE_CATEGORIES,
  type PartnerServiceCategoryRow,
} from '@/services/partnerServiceCategoryService'
import {
  PartnerServiceCategoryBadge,
  PartnerServiceCategoryForm,
  PartnerServiceCategoryHistoryModal,
} from '@/components/partners/PartnerServiceCategory'

type CoverageFilter = 'all' | 'with' | 'without'
type CategoryFilter = 'all' | 'none' | '1' | '2' | '3' | '4'
type BulkMode = 'tu' | 'category'

const moscowToday = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Moscow' })

export function PartnersCoveragePage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)

  const [rows, setRows] = useState<PartnerCoverageRow[]>([])
  const [managers, setManagers] = useState<TerritorialManagerListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<CoverageFilter>('all')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [chosenManager, setChosenManager] = useState<string>('')
  const [assignedFrom, setAssignedFrom] = useState(() => new Date().toISOString().slice(0, 10))
  const [busy, setBusy] = useState(false)
  const [isAdvanced, setIsAdvanced] = useState<boolean | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmStep, setConfirmStep] = useState<'review' | 'final'>('review')
  const [categories, setCategories] = useState<Map<number, PartnerServiceCategoryRow>>(new Map())
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')
  const [bulkMode, setBulkMode] = useState<BulkMode>('tu')
  const [historyFor, setHistoryFor] = useState<PartnerCoverageRow | null>(null)
  const [categoriesError, setCategoriesError] = useState<string | null>(null)

  const reload = () => {
    setLoading(true)
    // Категории грузятся отдельно: их сбой не должен ломать назначение ТУ.
    const categoriesLoad = listPartnerServiceCategories()
      .then(c => { setCategoriesError(null); return c })
      .catch((err): PartnerServiceCategoryRow[] => { setCategoriesError(err instanceof Error ? err.message : String(err)); return [] })
    Promise.all([listPartnerCoverage(), listTerritorialManagers(), categoriesLoad])
      .then(([r, m, c]) => {
        setRows(r)
        setManagers(m)
        setCategories(new Map(c.map(row => [row.partner_number, row])))
        setError(null)
      })
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    reload()
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!user?.id) {
      setIsAdvanced(false)
      return
    }
    getAccessLevel().then(level => {
      if (!cancelled) setIsAdvanced(level === 'advanced')
    })
    return () => { cancelled = true }
  }, [user?.id, getAccessLevel])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const num = q ? Number(q) : null
    return rows.filter(r => {
      if (filter === 'with' && !r.manager_user_id) return false
      if (filter === 'without' && r.manager_user_id) return false
      if (categoryFilter !== 'all') {
        const category = categories.get(r.partner_number)?.category ?? null
        if (categoryFilter === 'none' ? category !== null : String(category) !== categoryFilter) return false
      }
      if (!q) return true
      if (num !== null && !Number.isNaN(num) && r.partner_number === num) return true
      if (r.manager_name && r.manager_name.toLowerCase().includes(q)) return true
      if (r.persons_fio && r.persons_fio.toLowerCase().includes(q)) return true
      if (r.cities_text && r.cities_text.toLowerCase().includes(q)) return true
      return false
    })
  }, [rows, search, filter, categoryFilter, categories])

  // Популяция приходит из list_partner_coverage и уже сужена до действующих
  // франчайзи (миграция 20260820120000). Здесь ничего не досчитываем и не
  // дофильтровываем: второе определение «кто такой франчайзи» на клиенте
  // разъедется с серверным — по studio_count это уже не отличить (у бывшего
  // с незакрытым владением FOR_SALE он тоже был > 0).
  const stats = useMemo(() => {
    const total = rows.length
    const without = rows.filter(r => !r.manager_user_id).length
    // Строки, удержанные в выдаче только открытым назначением ТУ: франчайзи
    // уже не в сети, но снять назначение надо где-то.
    const stale = rows.filter(r => r.is_active_franchisee === false).length
    return { total, with: total - without, without, stale }
  }, [rows])

  // Категории считаются по той же популяции, что и покрытие ТУ (list_partner_coverage).
  const categoryStats = useMemo(() => {
    const byCategory = new Map<number | null, { partners: number; studios: number }>()
    const today = moscowToday()
    let reviewOverdue = 0
    for (const r of rows) {
      const c = categories.get(r.partner_number)
      const key = c?.category ?? null
      const cur = byCategory.get(key) ?? { partners: 0, studios: 0 }
      byCategory.set(key, { partners: cur.partners + 1, studios: cur.studios + (r.studio_count ?? 0) })
      if (c?.review_on && c.review_on < today) reviewOverdue++
    }
    return { byCategory, reviewOverdue }
  }, [rows, categories])

  const toggleSelect = (partnerNumber: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(partnerNumber)) next.delete(partnerNumber)
      else next.add(partnerNumber)
      return next
    })
  }

  const selectedRows = useMemo(
    () => rows.filter(r => selected.has(r.partner_number)),
    [rows, selected]
  )

  const transferCount = useMemo(
    () => selectedRows.filter(r => r.manager_user_id && r.manager_user_id !== chosenManager).length,
    [selectedRows, chosenManager]
  )

  const chosenManagerName = useMemo(
    () => managers.find(m => m.manager_user_id === chosenManager)?.manager_name
      || (chosenManager ? `#${chosenManager.slice(0, 8)}` : ''),
    [managers, chosenManager]
  )

  // Split selected partners into action buckets for the confirmation modal.
  const diff = useMemo(() => {
    const newAssign: PartnerCoverageRow[] = []
    const transfer: PartnerCoverageRow[] = []
    const dateChange: PartnerCoverageRow[] = []
    const noChange: PartnerCoverageRow[] = []
    for (const r of selectedRows) {
      if (!r.manager_user_id) newAssign.push(r)
      else if (r.manager_user_id !== chosenManager) transfer.push(r)
      else if (r.assigned_from !== assignedFrom) dateChange.push(r)
      else noChange.push(r)
    }
    return { newAssign, transfer, dateChange, noChange }
  }, [selectedRows, chosenManager, assignedFrom])

  const effectiveCount = diff.newAssign.length + diff.transfer.length + diff.dateChange.length

  const handleApply = async () => {
    if (!chosenManager || selected.size === 0) return
    setBusy(true)
    setActionError(null)
    try {
      for (const row of selectedRows) {
        if (row.manager_user_id === chosenManager) {
          if (row.assignment_id && row.assigned_from !== assignedFrom) {
            await updateTerritorialManagerAssignmentStart({
              assignmentId: row.assignment_id,
              assignedFrom,
            })
          }
          continue
        }
        if (row.manager_user_id) {
          await transferTerritorialManager({
            partnerNumber: row.partner_number,
            newManagerUserId: chosenManager,
            effectiveFrom: assignedFrom,
          })
        } else {
          await assignTerritorialManager({
            managerUserId: chosenManager,
            partnerNumber: row.partner_number,
            assignedFrom,
          })
        }
      }
      setSelected(new Set())
      setConfirmOpen(false)
      reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (isAdvanced === false) {
    return (
      <Card>
        <div className="text-sm text-muted-foreground">
          Эта страница доступна только пользователям УК с уровнем доступа «advanced».
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate('/management/territorial-managers')}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={14} /> К списку ТУ
      </button>

      <Card>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <div>Всего франчайзи: <span className="font-semibold">{stats.total}</span></div>
          <div>С ТУ: <span className="font-semibold text-success">{stats.with}</span></div>
          <div>Без ТУ: <span className="font-semibold text-error">{stats.without}</span></div>
          {stats.stale > 0 && (
            <div className="text-muted-foreground">
              Вышли из сети с открытым назначением: <span className="font-semibold text-warning">{stats.stale}</span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mt-2">
          {PARTNER_SERVICE_CATEGORIES.map(c => {
            const s = categoryStats.byCategory.get(c.value)
            return (
              <div key={c.value} title={c.services}>
                {c.label}: <span className="font-semibold">{s?.partners ?? 0}</span>
                <span className="text-xs text-muted-foreground"> · {s?.studios ?? 0} студ.</span>
              </div>
            )
          })}
          <div>
            Без категории:{' '}
            <span className={`font-semibold ${categoryStats.byCategory.get(null)?.partners ? 'text-warning' : ''}`}>
              {categoryStats.byCategory.get(null)?.partners ?? 0}
            </span>
          </div>
          {categoryStats.reviewOverdue > 0 && (
            <div>Пересмотр просрочен: <span className="font-semibold text-warning">{categoryStats.reviewOverdue}</span></div>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-2">
          Категория 4 — красная зона: отсмотры по студиям франчайзи не выдаются.
          Без категории франчайзи сопровождается как обычно.
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          Действующие франчайзи: есть студия в работе или в процессе открытия.
          Вышедшие из сети показываются, только пока за ними числится ТУ.
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Поиск: №франчайзи, ФИО, город или ТУ"
            className="w-full pl-9 pr-3 py-2 border border-border rounded-md bg-background text-sm"
          />
        </div>
        <div className="flex items-center gap-1 text-sm">
          <Filter size={14} className="text-muted-foreground" />
          {(['all', 'with', 'without'] as CoverageFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-md border text-xs ${
                filter === f
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border'
              }`}
            >
              {f === 'all' ? 'Все' : f === 'with' ? 'С ТУ' : 'Без ТУ'}
            </button>
          ))}
        </div>
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value as CategoryFilter)}
          className="px-3 py-1.5 border border-border rounded-md bg-background text-xs"
        >
          <option value="all">Все категории</option>
          {PARTNER_SERVICE_CATEGORIES.map(c => (
            <option key={c.value} value={String(c.value)}>{c.label}</option>
          ))}
          <option value="none">Без категории</option>
        </select>
      </div>

      {selected.size > 0 && (
        <Card className="border border-primary/30 bg-primary/5">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm font-semibold">Выбрано {selected.size} франчайзи:</div>
              {([['tu', 'Назначить ТУ'], ['category', 'Категория сопровождения']] as [BulkMode, string][]).map(([mode, label]) => (
                <button
                  key={mode}
                  onClick={() => setBulkMode(mode)}
                  className={`px-3 py-1 rounded-md border text-xs ${
                    bulkMode === mode ? 'bg-primary text-primary-foreground border-primary' : 'border-border'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {bulkMode === 'category' ? (
              <PartnerServiceCategoryForm
                selectedRows={selectedRows}
                categories={categories}
                today={moscowToday()}
                onDone={() => { setSelected(new Set()); reload() }}
                onCancel={() => setSelected(new Set())}
              />
            ) : (<>
            <div className="flex flex-wrap gap-2">
              <select
                value={chosenManager}
                onChange={e => setChosenManager(e.target.value)}
                className="flex-1 min-w-[200px] px-3 py-2 border border-border rounded-md bg-background text-sm"
              >
                <option value="">Выберите управляющего…</option>
                {managers.map(m => (
                  <option key={m.manager_user_id} value={m.manager_user_id}>
                    {m.manager_name || `#${m.manager_user_id.slice(0, 8)}`} · {m.partners_count} франч.
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={assignedFrom}
                onChange={e => setAssignedFrom(e.target.value)}
                className="px-3 py-2 border border-border rounded-md bg-background text-sm"
              />
              <Button
                size="sm"
                disabled={!chosenManager || busy}
                onClick={() => { setActionError(null); setConfirmStep('review'); setConfirmOpen(true) }}
              >
                Применить
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelected(new Set())}
              >
                Отменить
              </Button>
            </div>
            {transferCount > 0 && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertTriangle size={12} />
                {transferCount} франчайзи будут переданы от текущего ТУ; их предыдущий период будет закрыт {assignedFrom} − 1 день.
              </div>
            )}
            {actionError && (
              <div className="text-xs text-destructive">Ошибка: {actionError}</div>
            )}
            </>)}
          </div>
        </Card>
      )}

      {loading && <Card><div className="text-sm text-muted-foreground">Загрузка…</div></Card>}
      {categoriesError && (
        <Card><div className="text-sm text-destructive">Категории сопровождения не загрузились: {categoriesError}</div></Card>
      )}
      {error && <Card><div className="text-sm text-destructive">Ошибка: {error}</div></Card>}

      <div className="grid grid-cols-1 gap-2">
        {filtered.map(row => {
          const isSelected = selected.has(row.partner_number)
          return (
            <Card
              key={row.partner_number}
              className={isSelected ? 'ring-2 ring-primary' : ''}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelect(row.partner_number)}
                  className="h-4 w-4"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate">
                      {formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text)}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {row.studio_count} студий
                      {(row.onboarding_studio_count ?? 0) > 0 && (
                        <> · {row.onboarding_studio_count} в открытии</>
                      )}
                    </span>
                    {row.is_active_franchisee === false && (
                      <Badge text="вышел из сети" variant="error" />
                    )}
                    <PartnerServiceCategoryBadge
                      row={categories.get(row.partner_number)}
                      onClick={() => setHistoryFor(row)}
                    />
                  </div>
                  <div className="text-xs mt-0.5 flex items-center gap-2 flex-wrap">
                    {row.manager_user_id ? (
                      <>
                        <span className="text-muted-foreground">
                          ТУ: <span className="text-foreground">{row.manager_name}</span>
                          {row.assigned_from && <> · с {row.assigned_from}</>}
                        </span>
                        {row.assigned_to && (
                          <Badge text={`снятие ${row.assigned_to}`} variant="warning" />
                        )}
                      </>
                    ) : (
                      <Badge text="без ТУ" variant="warning" />
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )
        })}
        {!loading && filtered.length === 0 && (
          <Card><div className="text-sm text-muted-foreground">Ничего не найдено.</div></Card>
        )}
      </div>

      {historyFor && (
        <PartnerServiceCategoryHistoryModal
          partner={historyFor}
          canEdit={isAdvanced === true}
          onClose={() => setHistoryFor(null)}
          onChanged={reload}
        />
      )}

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => { if (!busy) setConfirmOpen(false) }}
        >
          <div
            className="bg-card rounded-card shadow-lg w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-border">
              <div className="text-sm font-semibold">Подтверждение назначения</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Назначить ТУ <span className="text-foreground font-medium">{chosenManagerName}</span>
                {' '}с <span className="text-foreground font-medium">{assignedFrom}</span>.
                Выбрано {selected.size}, изменений: {effectiveCount}.
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-sm">
              {effectiveCount === 0 && (
                <div className="text-muted-foreground">
                  Все выбранные франчайзи уже назначены этому ТУ с указанной даты — изменений не будет.
                </div>
              )}

              {diff.newAssign.length > 0 && (
                <DiffSection
                  title={`Новые назначения · ${diff.newAssign.length}`}
                  accent="success"
                >
                  {diff.newAssign.map(r => (
                    <DiffRow key={r.partner_number}>
                      <PartnerLabel row={r} />
                      <span className="text-xs text-muted-foreground">
                        ранее без ТУ → <span className="text-foreground">{chosenManagerName}</span> с {assignedFrom}
                      </span>
                    </DiffRow>
                  ))}
                </DiffSection>
              )}

              {diff.transfer.length > 0 && (
                <DiffSection
                  title={`Передача от другого ТУ · ${diff.transfer.length}`}
                  accent="warning"
                >
                  <div className="text-[11px] text-muted-foreground mb-1.5 flex items-center gap-1">
                    <AlertTriangle size={11} />
                    Предыдущее назначение закроется датой {assignedFrom} − 1 день
                  </div>
                  {diff.transfer.map(r => (
                    <DiffRow key={r.partner_number}>
                      <PartnerLabel row={r} />
                      <span className="text-xs text-muted-foreground">
                        от <span className="text-foreground">{r.manager_name}</span>
                        {r.assigned_from && <> (с {r.assigned_from})</>}
                        {' '}→ <span className="text-foreground">{chosenManagerName}</span> с {assignedFrom}
                      </span>
                    </DiffRow>
                  ))}
                </DiffSection>
              )}

              {diff.dateChange.length > 0 && (
                <DiffSection
                  title={`Изменение даты начала · ${diff.dateChange.length}`}
                  accent="neutral"
                >
                  {diff.dateChange.map(r => (
                    <DiffRow key={r.partner_number}>
                      <PartnerLabel row={r} />
                      <span className="text-xs text-muted-foreground">
                        тот же ТУ: дата {r.assigned_from ?? '—'} → <span className="text-foreground">{assignedFrom}</span>
                      </span>
                    </DiffRow>
                  ))}
                </DiffSection>
              )}

              {diff.noChange.length > 0 && (
                <DiffSection
                  title={`Без изменений · ${diff.noChange.length}`}
                  accent="muted"
                >
                  {diff.noChange.map(r => (
                    <DiffRow key={r.partner_number}>
                      <PartnerLabel row={r} />
                      <span className="text-xs text-muted-foreground">
                        уже у {chosenManagerName} с {r.assigned_from ?? '—'} — будет пропущен
                      </span>
                    </DiffRow>
                  ))}
                </DiffSection>
              )}

              {actionError && (
                <div className="text-xs text-destructive flex items-center gap-1">
                  <AlertTriangle size={12} /> {actionError}
                </div>
              )}
            </div>

            {confirmStep === 'final' && (
              <div className="px-4 py-3 border-t border-border bg-error/5">
                <div className="text-sm font-semibold text-error flex items-center gap-2">
                  <AlertTriangle size={14} />
                  Окончательное подтверждение
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Будет применено {effectiveCount} изменени{effectiveCount === 1 ? 'е' : 'й'}
                  {diff.transfer.length > 0 && <>, в т.ч. передача {diff.transfer.length} франчайзи от другого ТУ</>}
                  . Действие отменяется через карточку ТУ, но потребует ручных шагов.
                </div>
              </div>
            )}

            <div className="px-4 py-3 border-t border-border flex flex-wrap items-center justify-end gap-2">
              {confirmStep === 'review' ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setConfirmOpen(false)}
                  >
                    Отмена
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy || effectiveCount === 0}
                    onClick={() => setConfirmStep('final')}
                  >
                    Подтвердить ({effectiveCount})
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setConfirmStep('review')}
                  >
                    Назад
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy || effectiveCount === 0}
                    onClick={handleApply}
                  >
                    {busy ? 'Применяем…' : 'Да, применить'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface DiffSectionProps {
  title: string
  accent: 'success' | 'warning' | 'neutral' | 'muted'
  children: React.ReactNode
}

function DiffSection({ title, accent, children }: DiffSectionProps) {
  const accentClasses = {
    success: 'border-success/40 bg-success/5',
    warning: 'border-primary/40 bg-primary/5',
    neutral: 'border-border bg-muted/30',
    muted: 'border-border/60 bg-muted/10',
  }[accent]
  const titleClasses = {
    success: 'text-success',
    warning: 'text-primary',
    neutral: 'text-foreground',
    muted: 'text-muted-foreground',
  }[accent]
  return (
    <div className={`rounded-md border ${accentClasses} p-2.5`}>
      <div className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${titleClasses}`}>
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  )
}

function DiffRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      {children}
    </div>
  )
}

function PartnerLabel({ row }: { row: PartnerCoverageRow }) {
  return (
    <span className="text-sm font-medium">
      {formatPartnerDisplay(row.partner_number, row.persons_fio, row.cities_text)}
    </span>
  )
}
